import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Banner } from "../../../_components/Contacts/ContactShared";
import PurchaseDocList from "../../../_components/Purchasing/PurchaseDocList";
import {
    decideApproval,
    getPurchaseApprovalList,
    isFinalApprovalDecision,
} from "../../../_services/approval.service";
import {
    FiClock,
    FiUser,
    FiDollarSign,
    FiHome,
    FiCheckCircle,
    FiXCircle,
    FiFileText
} from "react-icons/fi";
import { T, fmtMYR, ApproveModal, RejectModal } from "../../../_components/Sales/SalesShared";
import { DocStatusBadge as PurchaseStatusBadge } from "../../../_components/Purchasing/PurchaseShared";

const APPROVAL_STATUSES = ["All", "Pending for Approval", "Approved", "Rejected"];

const PURCHASE_SEARCH_OPTIONS = [
    { key: "all", label: "All" },
    { key: "id", label: "PO No." },
    { key: "date", label: "Date" },
    { key: "supplier", label: "Company Name" },
    { key: "contactPerson", label: "Contact Person" }
];

export default function PurchasingApprovals() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [refreshKey, setRefreshKey] = useState(0);
    const [banner, setBanner] = useState({ type: "", msg: "" });
    const [approveTgt, setApproveTgt] = useState(null);
    const [rejectTgt, setRejectTgt] = useState(null);
    const [decidingKeys, setDecidingKeys] = useState(() => new Set());
    const bannerTimerRef = useRef(null);
    const decisionInFlightRef = useRef(new Set());

    const decisionKey = (item) => `${item.recordType}:${item.dbId}`;

    const showBanner = useCallback((type, msg) => {
        clearTimeout(bannerTimerRef.current);
        setBanner({ type, msg });
        bannerTimerRef.current = setTimeout(
            () => setBanner({ type: "", msg: "" }),
            4000
        );
    }, []);

    useEffect(() => () => clearTimeout(bannerTimerRef.current), []);

    // Default to "Pending for Approval" when first entering the page
    useEffect(() => {
        if (!searchParams.get("status")) {
            setSearchParams(
                {
                    status: "Pending for Approval",
                    page: "1"
                },
                { replace: true }
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getPurchaseApprovals = useCallback(
        async (qp, options = {}) => {
            void refreshKey;
            const result = await getPurchaseApprovalList(qp, options);
            return {
                ...result,
                data: (result.data || []).map((x) => ({
                    ...x,
                    _docType: "Purchase Order",
                    _basePath: "/purchases/purchase-orders",
                    _typeKey: x.typeKey,
                })),
            };
        },
        [refreshKey]
    );

    const handleApproveRejectPurchase = useCallback(
        async (item, action, onClose, reason = null) => {
            const key = decisionKey(item);
            if (decisionInFlightRef.current.has(key)) return false;
            decisionInFlightRef.current.add(key);
            setDecidingKeys((current) => new Set(current).add(key));
            try {
                const result = await decideApproval(item.recordType, item.dbId, action, reason);

                onClose();
                setRefreshKey((k) => k + 1);
                window.dispatchEvent(new CustomEvent("refreshPendingApprovalCounts"));
                const finalApproval = action === "approve" && isFinalApprovalDecision(result);
                showBanner(
                    "success",
                    action === "reject"
                        ? `Document ${item.id} rejected successfully.`
                        : finalApproval
                            ? `Document ${item.id} approved successfully.`
                            : `Approval for document ${item.id} recorded. Pending the next approval level.`
                );
                return true;
            } catch (err) {
                console.error(err);
                showBanner("danger", err.message || `Failed to process document ${item.id}.`);
                return false;
            } finally {
                decisionInFlightRef.current.delete(key);
                setDecidingKeys((current) => {
                    const next = new Set(current);
                    next.delete(key);
                    return next;
                });
            }
        },
        [showBanner]
    );

    const renderPurchasePreviewActions = useCallback(
        (item, onClose, onView) => {
            return (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%" }}>
                    {item.status === "Pending for Approval" && (item.canApprove || item.canReject) && (
                        <div style={{ display: "flex", gap: 10, width: "100%" }}>
                            {item.canApprove && <button
                                disabled={decidingKeys.has(decisionKey(item))}
                                onClick={() => setApproveTgt({ item, onClose })}
                                style={{
                                    flex: 1,
                                    padding: "9px 12px",
                                    background: T.success,
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 8,
                                    fontWeight: 600,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 6,
                                    cursor: "pointer"
                                }}
                            >
                                <FiCheckCircle size={14} /> Approve
                            </button>}
                            {item.canReject && <button
                                disabled={decidingKeys.has(decisionKey(item))}
                                onClick={() => setRejectTgt({ item, onClose })}
                                style={{
                                    flex: 1,
                                    padding: "9px 12px",
                                    background: "#fff",
                                    color: T.danger,
                                    border: `1.5px solid ${T.danger}`,
                                    borderRadius: 8,
                                    fontWeight: 600,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 6,
                                    cursor: "pointer"
                                }}
                            >
                                <FiXCircle size={14} /> Reject
                            </button>}
                        </div>
                    )}

                    <button
                        className="cl-preview-btn-primary"
                        onClick={() => onView(`${item._basePath}/${item.id}?fromApprovals=true`)}
                        style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            padding: "12px 16px",
                            fontSize: 14
                        }}
                    >
                        <FiFileText size={16} /> View Document Details
                    </button>
                </div>
            );
        },
        [decidingKeys]
    );

    const purchaseColumns = useMemo(
        () => [
            {
                label: "Doc. No.",
                key: "id",
                width: 110,
                render: (v) => (
                    <span style={{ color: T.textLight, fontWeight: 700, fontSize: 13 }}>{v}</span>
                )
            },
            {
                label: "Type",
                key: "_docType",
                width: 140,
                render: (v) => (
                    <span style={{ fontWeight: 600, color: T.accent, fontSize: 13 }}>{v}</span>
                )
            },
            {
                label: "Date",
                key: "date",
                width: 110,
                render: (v) => <span style={{ color: T.textMuted, fontSize: 13 }}>{v}</span>
            },
            {
                label: "Company Name",
                key: "supplier",
                width: 220,
                render: (v) => (
                    <span
                        style={{
                            fontWeight: 600,
                            display: "inline-block",
                            maxWidth: 210,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                        }}
                    >
                        {v}
                    </span>
                )
            },
            {
                label: "Contact Person",
                key: "contactPerson",
                render: (v) => <span style={{ color: T.textMuted, fontSize: 13 }}>{v || "—"}</span>
            },
            { label: "Status", key: "status", render: (v) => <PurchaseStatusBadge value={v} /> }
        ],
        []
    );

    const purchasePreviewFields = useMemo(
        () => [
            { icon: FiFileText, label: "Document Type", key: "_docType" },
            { icon: FiClock, label: "Date", key: "date" },
            { icon: FiHome, label: "Company Name", key: "supplier" },
            { icon: FiUser, label: "Contact Person", key: "contactPerson" },
            { icon: FiDollarSign, label: "Total Amount", key: "grandTotal", render: (v, row) => fmtMYR(v ?? row.payAmount) }
        ],
        []
    );

    return (
        <div style={{ padding: "0 20px" }}>
            <Banner type={banner.type} msg={banner.msg} onClose={() => setBanner({ type: "", msg: "" })} />

            <div style={{ marginBottom: 20 }}>
                <h4 style={{ fontWeight: 700, color: T.textPrimary, margin: 0, fontSize: 24 }}>
                    Purchase Approvals
                </h4>
                <p style={{ fontSize: 14, color: T.textMuted, margin: "4px 0 0" }}>
                    Review and process purchase documents submitted for approval.
                </p>
            </div>

            <ApproveModal
                target={approveTgt?.item}
                docType={approveTgt?.item?._docType}
                busy={approveTgt ? decidingKeys.has(decisionKey(approveTgt.item)) : false}
                onConfirm={async () => {
                    const processed = await handleApproveRejectPurchase(approveTgt.item, "approve", approveTgt.onClose);
                    if (processed) setApproveTgt(null);
                }}
                onCancel={() => setApproveTgt(null)}
            />

            <RejectModal
                target={rejectTgt?.item}
                docType={rejectTgt?.item?._docType}
                busy={rejectTgt ? decidingKeys.has(decisionKey(rejectTgt.item)) : false}
                onConfirm={async (reason) => {
                    const processed = await handleApproveRejectPurchase(rejectTgt.item, "reject", rejectTgt.onClose, reason);
                    if (processed) setRejectTgt(null);
                }}
                onCancel={() => setRejectTgt(null)}
            />

            <PurchaseDocList
                docType="Purchase Document"
                basePath="/purchases"
                fetchList={getPurchaseApprovals}
                statuses={APPROVAL_STATUSES}
                customColumns={purchaseColumns}
                renderPreviewActions={renderPurchasePreviewActions}
                hideCreate={true}
                hideExport={true}
                searchOptions={PURCHASE_SEARCH_OPTIONS}
                previewFields={purchasePreviewFields}
            />
        </div>
    );
}
